import { BarChartOutlined, DownOutlined, KeyOutlined, LogoutOutlined } from "@ant-design/icons";
import { Avatar, Button, Dropdown, Layout, Menu } from "antd";
import { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import type { CurrentUser } from "../../api/auth";
import { BrandIdentity } from "../../components/BrandIdentity";
import { getDefaultPathForRole, getNavigationForRole, type NavigationItem } from "./navigation";

const { Header } = Layout;

const ROLE_LABELS = { NORMAL_USER: "普通用户", REVIEWER: "审核员", ADMIN: "管理员" } as const;

function resolveSelectedKey(pathname: string, items: readonly NavigationItem[]): string {
  return items.find((item) => pathname.startsWith(item.path))?.key ?? "";
}

interface AppHeaderProps {
  user: CurrentUser;
  logoutPending: boolean;
  onChangePassword: () => void;
  onLogout: () => void;
}

export function AppHeader({ user, logoutPending, onChangePassword, onLogout }: AppHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = getNavigationForRole(user.role);
  const menuItems = useMemo(
    () =>
      navigation.map((item) => ({
        key: item.key,
        label: <Link to={item.path}>{item.label}</Link>,
      })),
    [navigation],
  );
  const accountMenuItems = useMemo(
    () => [
      ...(user.role === "ADMIN"
        ? [
            {
              key: "platform-statistics",
              icon: <BarChartOutlined />,
              label: "平台统计",
            },
          ]
        : []),
      { key: "password", icon: <KeyOutlined />, label: "修改密码" },
      { type: "divider" as const },
      { key: "logout", icon: <LogoutOutlined />, label: "退出登录", danger: true },
    ],
    [user.role],
  );

  const handleAccountMenuClick = (key: string) => {
    if (key === "platform-statistics") {
      void navigate("/admin/platform-statistics");
    } else if (key === "password") {
      onChangePassword();
    } else if (key === "logout") {
      onLogout();
    }
  };

  return (
    <Header className="app-header">
      <Link className="brand-link" to={getDefaultPathForRole(user.role)} aria-label="返回默认页面">
        <BrandIdentity showSubtitle={false} />
      </Link>

      <nav className="top-navigation-shell" aria-label="主导航">
        <Menu
          className="top-navigation"
          mode="horizontal"
          items={menuItems}
          selectedKeys={[resolveSelectedKey(location.pathname, navigation)]}
        />
      </nav>

      <div className="header-account">
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          menu={{
            items: accountMenuItems,
            onClick: ({ key }) => handleAccountMenuClick(key),
          }}
        >
          <Button
            className="account-trigger"
            type="text"
            loading={logoutPending}
            aria-label={`打开${user.name}的账号菜单`}
          >
            <Avatar src={user.avatar_url} className="account-avatar" size={34}>
              {user.avatar_text}
            </Avatar>
            <span className="account-copy">
              <strong>{user.name}</strong>
              <small>{ROLE_LABELS[user.role]}</small>
            </span>
            <DownOutlined className="account-chevron" />
          </Button>
        </Dropdown>
      </div>
    </Header>
  );
}
